/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IActionContext, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import {
    Button,
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    Node,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import {
    ChannelDeliveryOptionConfiguration,
    ChannelIdentity,
    Customer,
    OrderOriginator,
    OrgUnitLocation,
    SalesOrder,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Observer } from 'mobx-react';
import React from 'react';

import classnames from 'classnames';

import { getGroups, IGroup, IGroupDelivery, IGroups } from '@msdyn365-commerce-modules/order-management';
import { getOrderInformation, getOrderInformationInput, IOrderInformation } from '@msdyn365-commerce-modules/order-management';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { getSalesLines, getSalesLinesInput, IGetSalesLinesInput } from '@msdyn365-commerce-modules/order-management';
import { ExpandButtonState, IPriceContext, SalesLineState } from '@msdyn365-commerce-modules/order-management';
import {
    IPhibroCOrderHistoryWithSearchAndFiltersProps,
    IPhibroCOrderHistoryWithSearchAndFiltersResources
} from '../phibro-c-order-history-with-search-and-filters.props.autogenerated';
import { IPhibroCOrderHistoryWithSearchAndFiltersData } from '../phibro-c-order-history-with-search-and-filters.data';
import { getOrderDetailsPageUrl } from '../common/get-sales-order';
import { renderOrderInformationAmount } from '../../../themes/phibro-b2b/utils/order-amount-display';
import { resolveSalesOrderPoNumberAsync } from '../../../themes/phibro-b2b/utils/sales-order-po-number';
import { getProductTilePlaceholderUrl } from '../../../themes/phibro-b2b/utils/placeholder-image';

/**
 * Class names used in component.
 */
const componentClassName = 'ms-order-history-with-search-and-filters-order-list';
const orderListItemClassName = `${componentClassName}__sales-order`;
const orderInformationHeaderClassName = `${componentClassName}__sales-order__header`;
const expandProductsButtonClassName = `${componentClassName}__sales-order__expand-products-button`;
const expandProductsButtonWrapperClassName = `${componentClassName}__sales-order__expand-products-button-wrapper`;
const orderDetailButtonClassName = `${componentClassName}__btn-order-details`;
const moduleName = componentClassName;

type OrderInformationWithPoProps = {
    orderInformation: IOrderInformation;
    salesOrder: SalesOrder;
    actionContext: IActionContext;
    salesLineCount: number;
};

const getJdeOrderNumber = (salesOrder: SalesOrder): string | undefined => {
    // Listing API: JDEOrderNumber is available directly in SalesOrder ExtensionProperties
    const jdeOrderNumberProperty = salesOrder.ExtensionProperties?.find(property => property.Key === 'JDEOrderNumber');

    if (jdeOrderNumberProperty?.Value?.StringValue) {
        return jdeOrderNumberProperty.Value.StringValue;
    }

    // Search API: JDEOrderNumber is available in SalesLines ExtensionProperties
    const salesLine = salesOrder.SalesLines?.[0];

    const jdeSalesOrderRefProperty = salesLine?.ExtensionProperties?.find(property => property.Key === 'JDESalesOrderRefNumber');

    if (!jdeSalesOrderRefProperty?.Value?.StringValue) {
        return undefined;
    }

    try {
        const orderReferences = JSON.parse(jdeSalesOrderRefProperty.Value.StringValue);

        return orderReferences?.[0]?.JDESalesOrderNumber || undefined;
    } catch {
        return undefined;
    }
};

const OrderInformationWithPoNumber: React.FC<OrderInformationWithPoProps> = ({
    orderInformation,
    salesOrder,
    actionContext,
    salesLineCount
}) => {
    const [poNumber, setPoNumber] = React.useState<string | undefined>();
    const jdeOrderNumber = getJdeOrderNumber(salesOrder);

    React.useEffect(() => {
        let cancelled = false;

        void resolveSalesOrderPoNumberAsync(actionContext, salesOrder).then(value => {
            if (!cancelled && value) {
                setPoNumber(value);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [actionContext, salesOrder]);

    const poNumberElement =
        poNumber && poNumber.length > 0 ? (
            <p className='ms-order-history__order-information-po-number'>
                <span className='ms-order-history__order-information-po-number-label'>PO Number: </span>
                <span className='ms-order-history__order-information-po-number-value'>{poNumber}</span>
            </p>
        ) : (
            undefined
        );

    return (
        <OrderInfomationComponent
            {...orderInformation}
            count={
                <div className='ms-order-history-with-search-and-filters-order-list__sales-order__header'>
                    <span className='ms-order-history-with-search-and-filters-order-list__order-information-count'>
                        {salesLineCount} {salesLineCount === 1 ? 'line' : 'lines'}
                    </span>
                </div>
            }
            poNumber={poNumberElement}
            jdeOrderNumber={jdeOrderNumber}
        />
    );
};

type OrderInformationWithPoDisplayProps = IOrderInformation & {
    poNumber?: React.ReactNode;
    jdeOrderNumber?: string;
};

/**
 * IOrderHistoryWithSearchAndFiltersOrderListData interface.
 */
export interface IOrderHistoryWithSearchAndFiltersOrderListData {
    salesOrders: SalesOrder[];
    products: SimpleProduct[];
    channelIdentities?: ChannelIdentity[];
    orgUnitLocations?: OrgUnitLocation[];
    customer?: Customer;
    originators?: OrderOriginator[];
}

/**
 * IOrderHistoryWithSearchAndFiltersOrderListProps interface.
 */
export interface IOrderHistoryWithSearchAndFiltersOrderListProps extends IComponentProps<IOrderHistoryWithSearchAndFiltersOrderListData> {
    moduleProps: IPhibroCOrderHistoryWithSearchAndFiltersProps<IPhibroCOrderHistoryWithSearchAndFiltersData>;
    className?: string;
    resources: IPhibroCOrderHistoryWithSearchAndFiltersResources;
    isMobileView?: boolean;
    showChannelInfo: boolean;
    showOrderPlacedBy?: boolean;
    salesLinesLimit?: number;
    telemetryContent?: ITelemetryContent;
    retailMultiplePickUpOptionEnabled?: boolean;
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
}

/**
 * IOrderHistoryWithSearchAndFiltersOrderItemProps interface.
 */
interface IOrderHistoryWithSearchAndFiltersOrderItemProps {
    orderInfomation: IOrderInformation;
    groups?: IGroups;
    orderDetailsLink: React.ReactNode;
    expandProductsButton?: React.ReactNode;
}

/**
 * OrderInfomation component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const OrderInfomationComponent: React.FC<OrderInformationWithPoDisplayProps> = ({
    orderInformationProps,
    placedBy,
    createdDate,
    count,
    amount,
    poNumber,
    jdeOrderNumber
}) => (
    <Node {...orderInformationProps}>
        {placedBy}

        <div className={orderInformationHeaderClassName}>{count}</div>

        <p className='ms-order-history__order-information-jde-order-number'>
            <span className='ms-order-history__order-information-jde-order-number-label'>Order Number: </span>
            <span className='ms-order-history__order-information-jde-order-number-value'>{jdeOrderNumber || ''}</span>
        </p>

        {createdDate}
        {renderOrderInformationAmount(amount)}
        {poNumber}
    </Node>
);

/**
 * GroupDelivery component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const GroupDeliveryComponent: React.FC<IGroupDelivery> = ({ deliveryProps, heading, count, processing, address, trackingInfo }) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {processing}
        {address}
        {trackingInfo}
    </Node>
);

/**
 * Group component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const GroupComponent: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <GroupDeliveryComponent {...delivery} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map((salesLine: ISalesLine) => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>{salesLine.salesLine}</React.Fragment>
                ))}
            </Node>
        )}
    </Node>
);

/**
 * Groups component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const GroupsComponent: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <GroupComponent key={index} {...group} />
        ))}
    </Node>
);

/**
 * Updates product states to show all products.
 * @param expandButtonState - Expand button state.
 * @param productStates - Product states.
 * @returns Callback method.
 */
const onExpandProductsCallback = (expandButtonState: ExpandButtonState, productStates: SalesLineState[]) => () => {
    for (const productState of productStates) {
        productState.isHidden = false;
    }
    expandButtonState.extraProductsCount = 0;
};

/**
 * Gets the IOrderHistoryWithSearchAndFiltersOrderItem configuration.
 * @param salesOrder The sales order
 * @param index The sales order index
 * @param props The component props
 * @returns The IOrderHistoryWithSearchAndFiltersOrderItem configuration.
 */
const createOrderHistoryWithSearchAndFiltersOrderItemProps = (
    salesOrder: SalesOrder,
    index: number,
    props: IOrderHistoryWithSearchAndFiltersOrderListProps
): IOrderHistoryWithSearchAndFiltersOrderItemProps => {
    const {
        id,
        typeName,
        context,
        moduleProps: { telemetry },
        data: { products, channelIdentities, orgUnitLocations, customer, originators },
        showChannelInfo,
        telemetryContent,
        resources: { freePriceText, detailsAriaLabel, detailsLabel, orderItemLabel, orderItemsLabel },
        context: { actionContext },
        retailMultiplePickUpOptionEnabled,
        channelDeliveryOptionConfig
    } = props;

    const priceContext = {
        id,
        typeName,
        context,
        telemetry,
        freePriceText
    } as IPriceContext;

    const originator: OrderOriginator | undefined = originators?.find(
        originator =>
            (salesOrder.SalesId && originator.SalesId === salesOrder.SalesId) ||
            (salesOrder.Id && originator.TransactionId === salesOrder.Id)
    );

    const salesLinesInput =
        products &&
        getSalesLinesInput(
            props.moduleProps,
            moduleName,
            salesOrder,
            products,
            priceContext,
            undefined,
            undefined,
            undefined,
            retailMultiplePickUpOptionEnabled,
            channelDeliveryOptionConfig
        );
    let salesLines: ISalesLine[] | undefined;
    let expandProductsButton: React.ReactNode;

    if (salesLinesInput) {
        const getSalesLinesInputProps: IGetSalesLinesInput = {
            ...salesLinesInput,
            imageOnly: true
        };

        if (!ObjectExtensions.isNullOrUndefined(props.salesLinesLimit)) {
            const salesLinesLimit = 2;

            const extraProductsCount = getSalesLinesInputProps.salesLines.length - salesLinesLimit;

            getSalesLinesInputProps.salesLineStates = getSalesLinesInputProps.salesLines.map(
                (salesLine, index) => new SalesLineState(`${salesLine.LineId ?? ''}-${index}`, index >= salesLinesLimit)
            );

            if (extraProductsCount > 0) {
                const expandButtonState = new ExpandButtonState(extraProductsCount);

                expandProductsButton = (
                    <>
                        <Observer>
                            {() => {
                                const text = format(
                                    props.resources.orderHistoryExpandProductsButtonText,
                                    expandButtonState.extraProductsCount
                                );

                                return (
                                    expandButtonState.extraProductsCount > 0 && (
                                        <Button
                                            className={expandProductsButtonClassName}
                                            title={text}
                                            role='button'
                                            onClick={onExpandProductsCallback(expandButtonState, getSalesLinesInputProps.salesLineStates!)}
                                            aria-label={text}
                                        >
                                            {text}
                                        </Button>
                                    )
                                );
                            }}
                        </Observer>
                    </>
                );
            }
        }

        salesLines = getSalesLines(getSalesLinesInputProps);
    }

    expandProductsButton = (
        <>
            {expandProductsButton}
            <div className={expandProductsButtonWrapperClassName} />
        </>
    );
    const groups =
        salesLines &&
        getGroups({
            moduleName,
            salesLines,
            resources: { orderItemLabel, orderItemsLabel },
            retailMultiplePickUpOptionEnabled
        });

    const orderInfomationInput = getOrderInformationInput(
        props.moduleProps,
        moduleName,
        salesOrder,
        priceContext,
        channelIdentities,
        orgUnitLocations,
        customer,
        originator,
        showChannelInfo
    );

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.OrderDetails);
    const orderDetailsAttributes = getTelemetryAttributes(telemetryContent!, payLoad);

    const orderDetailsLink = (
        <Button
            href={getOrderDetailsPageUrl(actionContext, salesOrder)}
            role='button'
            aria-label={
                salesOrder.ChannelReferenceId && detailsAriaLabel
                    ? detailsAriaLabel.replace('{orderId}', salesOrder.ChannelReferenceId)
                    : ''
            }
            className={orderDetailButtonClassName}
            {...orderDetailsAttributes}
        >
            {detailsLabel}
        </Button>
    );

    return {
        orderInfomation: getOrderInformation(orderInfomationInput),
        groups: groups,
        orderDetailsLink: orderDetailsLink,
        expandProductsButton: expandProductsButton
    };
};

/**
 * OrderHistoryWithSearchAndFiltersOrderList component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const OrderHistoryWithSearchAndFiltersOrderListFunctionalComponent: React.FC<IOrderHistoryWithSearchAndFiltersOrderListProps> = (
    props: IOrderHistoryWithSearchAndFiltersOrderListProps
) => {
    const placeholderName = props.context?.app?.config?.placeholderImageName?.trim();
    const placeholderUrl = placeholderName
        ? getProductTilePlaceholderUrl(placeholderName, props.context?.request?.apiSettings?.baseImageUrl)
        : undefined;
    const placeholderStyle = placeholderUrl
        ? ({ '--cart-placeholder-image': `url("${placeholderUrl}")` } as React.CSSProperties)
        : undefined;

    return (
        <Node className={classnames(props.className, componentClassName)} style={placeholderStyle}>
            {ArrayExtensions.hasElements(props.data.salesOrders) &&
                props.data.salesOrders.map((salesOrder: SalesOrder, index: number) => {
                    const itemProps: IOrderHistoryWithSearchAndFiltersOrderItemProps = createOrderHistoryWithSearchAndFiltersOrderItemProps(
                        salesOrder,
                        index,
                        props
                    );
                    return (
                        <Node className={orderListItemClassName} key={salesOrder.SalesId || salesOrder.Id}>
                            {itemProps.orderInfomation && (
                                <OrderInformationWithPoNumber
                                    orderInformation={itemProps.orderInfomation}
                                    salesOrder={salesOrder}
                                    actionContext={props.context.actionContext}
                                    salesLineCount={salesOrder.SalesLines?.length ?? 0}
                                />
                            )}
                            {itemProps.groups && <GroupsComponent {...itemProps.groups} />}
                            {itemProps.expandProductsButton}
                            {itemProps.orderDetailsLink}
                        </Node>
                    );
                })}
        </Node>
    );
};

/**
 * OrderHistoryWithSearchAndFiltersOrderList component.
 */
export const OrderHistoryWithSearchAndFiltersOrderListComponent: React.FunctionComponent<IOrderHistoryWithSearchAndFiltersOrderListProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error
    IOrderHistoryWithSearchAndFiltersOrderListProps
>('OrderHistoryWithSearchAndFiltersOrderListComponent', { component: OrderHistoryWithSearchAndFiltersOrderListFunctionalComponent });
